import { useEffect, useState } from "react";
import api from "../api/api";
import Card from "../components/Card";

import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Area,
  ResponsiveContainer,
} from "recharts";

export default function Dashboard() {
  const [stats, setStats] = useState({});
  const [statusChart, setStatusChart] = useState([]);
  const [companyMeetings, setCompanyMeetings] = useState([]);
  const [meetings, setMeetings] = useState([]);

  const COLORS = ["#6366f1", "#22c55e", "#f59e0b", "#ef4444"];

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      const [s, status, company, m] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/dashboard/meeting-status-chart"),
        api.get("/dashboard/meetings-per-company"),
        api.get("/dashboard/upcoming-meetings"),
      ]);

      setStats(s.data);
      setStatusChart(
        status.data.map((item) => ({
          status: item.status || "Unknown",
          total: Number(item.total),
        })),
      );
      setCompanyMeetings(
        company.data.map((item) => ({
          company_name: item.company_name,
          total_meetings: Number(item.total_meetings),
        })),
      );
      setMeetings(m.data);
    } catch (err) {
      console.log(err);
    }
  };

  // 🔥 FULL CRM GRAPH
  const statsGraph = [
    { name: "Companies", value: Number(stats.total_companies) || 0 },
    { name: "Contacts", value: Number(stats.total_contacts) || 0 },
    { name: "Meetings", value: Number(stats.total_meetings) || 0 },
  ];

  return (
    <div className="min-h-screen -mt-10">
        <div className="max-w-5xl mx-auto">
      <div className="mb-10">
        <h1 className="text-4xl font-semibold text-center text-[#030244]">CRM Dashboard</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-6">
        <Card
          title="Companies"
          value={stats.total_companies}
          color="bg-[#050463]"
        />
        <Card
          title="Contacts"
          value={stats.total_contacts}
          color="bg-[#f0610f]"
        />
        <Card
          title="Meetings"
          value={stats.total_meetings}
          color="bg-[#12b17c]"
        />
        <Card
          title="Pending"
          value={stats.pending_meetings}
          color="bg-[#e4b005]"
        />
        <Card
          title="Completed"
          value={stats.completed_meetings}
          color="bg-[#0e9941]"
        />
        <Card
          title="FollowUps"
          value={stats.total_followups}
          color="bg-[#046fad]"
        />
        <Card
          title="Reminders"
          value={stats.total_reminders}
          color="bg-[#ee1212]"
        />
      </div>


      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

         {/* CRM Overview */}
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-lg shadow-lg p-6 mt-10 border-3  transition-all duration-300 hover:shadow-xl">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h3 className="font-semibold text-[#050463] text-2xl tracking-wide">
                CRM Overview
              </h3>
            </div>

            <div className="bg-[#030244] text-white text-[17px] px-3 py-1 rounded-lg font-medium">
              {statsGraph.length} Metrics
            </div>
          </div>

          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={statsGraph}
              margin={{ top: 10, right: 20, left: -20, bottom: 5 }}
            >
              <defs>
                <linearGradient id="companyBar" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#050463" stopOpacity={1} />
                  {/* <stop offset="100%" stopColor="#6366f1" stopOpacity={0.6} /> */}
                </linearGradient>

                <linearGradient id="contactBar" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#e4b005" stopOpacity={1} />
                  {/* <stop offset="100%" stopColor="#ec4899" stopOpacity={0.6} /> */}
                </linearGradient>

                <linearGradient id="meetingBar" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#12b17c" stopOpacity={1} />
                  {/* <stop offset="100%" stopColor="#22c55e" stopOpacity={0.6} /> */}
                </linearGradient>
              </defs>

              <CartesianGrid
                strokeDasharray="4 4"
                stroke="#e5e7eb"
                vertical={false}
              />

              <XAxis
                dataKey="name"
                tick={{ fontSize: 16, fill: "#25272b" }}
                axisLine={false}
                tickLine={false}
                dy={5}
              />

              <YAxis
                tick={{ fontSize: 15, fill: "#25272b" }}
                axisLine={false}
                tickLine={false}
              />

              <Tooltip
                cursor={false}
                contentStyle={{
                  borderRadius: "8px",
                  border: "none",
                  background: "#ffffff",
                  boxShadow: "0 10px 15px rgba(0,0,0,0.15)",
                }}
                labelStyle={{
                  fontWeight: "600",
                  color: "#1e0674",
                  fontSize: "18px",
                }}
              />

              <Bar dataKey="value" radius={[8, 8, 0, 0]} barSize={55}>
                {statsGraph.map((entry, index) => (
                  <Cell
                    key={index}
                    fill={
                      entry.name === "Companies"
                        ? "url(#companyBar)"
                        : entry.name === "Contacts"
                          ? "url(#contactBar)"
                          : "url(#meetingBar)"
                    }
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* MEETING STATUS */}
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-lg shadow-lg p-6 mt-10 border-3 hover:shadow-xl">
          <div className="flex justify-between items-center mb-3">
            <div>
              <h3 className="font-semibold text-[#030244] text-2xl">
                Meeting Status
              </h3>
            </div>

            <div className="text-[17px] bg-[#030244] font-medium text-white px-4 py-1.5 rounded-lg">
              {statusChart.length} Types
            </div>
          </div>

          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={statusChart}
                dataKey="total"
                nameKey="status"
                innerRadius={0}
                outerRadius={100}
                paddingAngle={3}
                cornerRadius={6}
                stroke="none"
                labelLine={false}
                label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
              >
                {statusChart.map((entry, i) => (
                  <Cell
                    key={i}
                    fill={
                      entry.status === "Completed"
                        ? "#0e9941"
                        : entry.status === "Pending"
                          ? "#f5610c"
                          : entry.status === "Cancelled"
                            ? "#ee1212"
                            : "#0e108b"
                    }
                  />
                ))}
              </Pie>

              <Tooltip
                formatter={(value, name) => [`${value}`, name]}
                contentStyle={{
                  borderRadius: "8px",
                  border: "1px solid #eee",
                  background: "#fff",
                  boxShadow: "0 6px 15px rgba(0,0,0,0.08)",
                }}
                labelStyle={{
                  fontWeight: "600",
                  color: "#111827",
                }}
              />
            </PieChart>
          </ResponsiveContainer>

          <div className="flex flex-wrap gap-5 mt-4 text-lg justify-center text-gray-800">
            {statusChart.map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                <span
                  className="w-2.5 h-2.5 rounded-full"
                  style={{
                    backgroundColor:
                      item.status === "Completed"
                        ? "#0e9941"
                        : item.status === "Pending"
                          ? "#f5610c"
                          : item.status === "Cancelled"
                            ? "#ee1212"
                            : "#0e108b",
                  }}
                />
                {item.status}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gradient-to-br from-white to-gray-50 p-6 rounded-lg shadow-lg mt-10 border-3 transition-all duration-300 hover:shadow-xl">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h3 className="font-semibold text-[#030244] text-2xl tracking-wide">
                Meetings Per Company
              </h3>
            </div>

            <div className="bg-[#030244] text-white text-[17px] px-4 py-1.5 rounded-lg font-medium">
              {companyMeetings.length} Companies
            </div>
          </div>

          <ResponsiveContainer width="100%" height={320}>
            <LineChart
              data={companyMeetings}
              margin={{ top: 10, right: 25, left: -10, bottom: 5 }}
            >
              <CartesianGrid
                strokeDasharray="4 4"
                stroke="#aabadb"
                vertical={false}
              />

              <XAxis
                dataKey="company_name"
                tick={{ fontSize: 16, fill: "#25272b" }}
                axisLine={false}
                tickLine={false}
                dy={5}
                dx={10}
              />

              <YAxis
                width={45}
                tick={{ fontSize: 15, fill: "#25272b" }}
                axisLine={false}
                tickLine={false}
                dx={-5}
              />

              <Tooltip
                cursor={{ stroke: "#030244", strokeWidth: 1.5 }}
                contentStyle={{
                  borderRadius: "12px",
                  border: "none",
                  background: "white",
                  boxShadow: "0 10px 15px rgba(0,0,0,0.15)",
                }}
                labelStyle={{
                  color: "#1e0674",
                  fontWeight: "600",
                  fontSize: "18px",
                }}
              />

              <defs>
                <linearGradient id="colorMeetings" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#030244" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#030244" stopOpacity={0} />
                </linearGradient>
              </defs>

              <Area
                type="monotone"
                dataKey="total_meetings"
                stroke="none"
                fill="url(#colorMeetings)"
              />

              <Line
                type="monotone"
                dataKey="total_meetings"
                stroke="#030244"
                strokeWidth={2}
                dot={{ r: 4, strokeWidth: 2, fill: "#fff" }}
                activeDot={{
                  r: 6,
                  fill: "#030244",
                  stroke: "#fff",
                  strokeWidth: 2,
                }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* UPCOMING MEETINGS */}
        <div className="bg-gradient-to-br from-white to-gray-50 p-6 rounded-lg shadow-lg mt-10 border-3 hover:shadow-xl">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="font-semibold text-gray-800 text-2xl">
                Upcoming Meetings
              </h3>
            </div>

            <div className="bg-[#030244] text-white text-[17px] px-4 py-1.5 rounded-lg font-medium">
              {meetings.length} Scheduled
            </div>
          </div>

          <div className="space-y-4 max-h-[360px] overflow-y-auto pr-1">
            {meetings.map((m) => {
              const meetingTime = new Date(m.schedule_at);

              return (
                <div
                  key={m.meeting_id}
                  className="group flex items-center justify-between p-4 rounded-xl bg-white border border-gray-400 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 mt-2"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 flex items-center justify-center rounded-full bg-gradient-to-b from-[#08b857] to-[#09477a] text-white font-semibold shadow text-xl">
                      {m.contact_name?.charAt(0)}
                    </div>

                    <div>
                      <p className="font-semibold text-[#030244] group-hover:text-[#090794] transition text-lg">
                        {m.contact_name}
                      </p>

                      <p className="text-sm text-gray-600 truncate max-w-[220px]">
                        {m.purpose}
                      </p>
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="bg-[#030244] text-white px-3 py-1 rounded-lg text-[14px] font-semibold mb-1">
                      {meetingTime.toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>

                    <p className="text-sm text-center text-gray-600">
                      {meetingTime.toLocaleDateString()}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
    </div>
  );
}

